import React, { useCallback, useEffect, useState } from 'react';
import { TrashIcon, XMarkIcon } from '@heroicons/react/24/outline';
import {
  disableProjectWebhookConfig,
  getProjectWebhookConfig,
  updateProjectWebhookConfig,
} from '../../utils/api';
import ButtonComponent from '../customButton';
import InputBox from '../FormComponent/InputBox';
import TextSkeletonContainer from '../Skeleton/TextSkeletonContainer';
import styles from './ProjectNotificationSettings.module.scss';

const WEBHOOK_COPY = {
  heading: 'Webhook configuration',
  description: 'Send project notification events to a webhook endpoint.',
  urlLabel: 'Webhook URL',
  urlPlaceholder: 'https://example.com/webhooks/project-events',
  save: 'Save',
  rotateSecret: 'Rotate secret',
  disable: 'Disable webhook',
  emptyTitle: 'No webhook configured',
  emptyDescription: 'Add a webhook URL to start receiving project notification events.',
  loadError: 'Unable to load the webhook configuration.',
  saveError: 'Unable to save the webhook configuration.',
  disableError: 'Unable to disable the webhook.',
  urlRequired: 'Enter a webhook URL before saving.',
  retry: 'Try again',
  secretTitle: 'Copy this secret now',
  secretDescription: 'This webhook secret is shown only once and cannot be recovered later.',
  dismissSecret: 'Dismiss secret notice',
};

const getErrorMessage = (error, fallback) => error?.response?.data?.message || fallback;
const getResponseData = (response) => response?.data?.data ?? null;

const WebhookConfigTab = ({ projectId }) => {
  const [config, setConfig] = useState(null);
  const [webhookUrl, setWebhookUrl] = useState('');
  const [secret, setSecret] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isDisabling, setIsDisabling] = useState(false);
  const [error, setError] = useState('');

  const loadConfig = useCallback(async () => {
    if (!projectId) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const response = await getProjectWebhookConfig(projectId);
      const webhookConfig = getResponseData(response);
      const isActive = webhookConfig?.status === 'active';
      setConfig(isActive ? webhookConfig : null);
      setWebhookUrl(isActive ? (webhookConfig?.webhookUrl || '') : '');
    } catch (requestError) {
      setError(getErrorMessage(requestError, WEBHOOK_COPY.loadError));
    } finally {
      setIsLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  const saveConfig = async () => {
    const nextUrl = webhookUrl.trim();
    if (!nextUrl) {
      setError(WEBHOOK_COPY.urlRequired);
      return;
    }

    setIsSaving(true);
    setError('');

    try {
      const response = await updateProjectWebhookConfig(projectId, { webhookUrl: nextUrl });
      const nextConfig = getResponseData(response);
      setConfig(nextConfig);
      setWebhookUrl(nextConfig?.webhookUrl || nextUrl);
      setSecret(nextConfig?.secret || '');
    } catch (requestError) {
      setError(getErrorMessage(requestError, WEBHOOK_COPY.saveError));
    } finally {
      setIsSaving(false);
    }
  };

  const disableConfig = async () => {
    setIsDisabling(true);
    setError('');

    try {
      await disableProjectWebhookConfig(projectId);
      setConfig(null);
      setWebhookUrl('');
      setSecret('');
    } catch (requestError) {
      setError(getErrorMessage(requestError, WEBHOOK_COPY.disableError));
    } finally {
      setIsDisabling(false);
    }
  };

  if (isLoading) {
    return (
      <div className={styles.config}>
        <TextSkeletonContainer width="100%" height={20} lines={2} />
      </div>
    );
  }

  if (error && !config && !webhookUrl) {
    return (
      <div className={`${styles.status} ${styles.error}`} role="alert">
        <p>{error}</p>
        <ButtonComponent
          ctaText={WEBHOOK_COPY.retry}
          variant="secondary"
          size="small"
          onClick={loadConfig}
        />
      </div>
    );
  }

  const isUrlError = error === WEBHOOK_COPY.urlRequired;
  const hasUrlChanged = webhookUrl.trim() !== (config?.webhookUrl || '').trim();

  return (
    <section className={styles.config} aria-labelledby="webhook-config-heading">
      <header className={styles.header}>
        <h2 id="webhook-config-heading">{WEBHOOK_COPY.heading}</h2>
        <p>{WEBHOOK_COPY.description}</p>
      </header>

      {!config && (
        <div className={styles.emptyState}>
          <h3>{WEBHOOK_COPY.emptyTitle}</h3>
          <p>{WEBHOOK_COPY.emptyDescription}</p>
        </div>
      )}

      {error && !isUrlError && <p className={styles.error} role="alert">{error}</p>}

      {secret && (
        <div className={styles.secretNotice} role="status">
          <div>
            <h3>{WEBHOOK_COPY.secretTitle}</h3>
            <p>{WEBHOOK_COPY.secretDescription}</p>
            <code className={styles.secret}>{secret}</code>
          </div>
          <ButtonComponent
            type="iconbutton"
            variant="outline"
            size="small"
            startIcon={<XMarkIcon className="icon blue700" />}
            onClick={() => setSecret('')}
            tooltip={WEBHOOK_COPY.dismissSecret}
          />
        </div>
      )}

      <div className={styles.form}>
        <InputBox
          id="project-webhook-url"
          name="webhookUrl"
          type="url"
          label={WEBHOOK_COPY.urlLabel}
          value={webhookUrl}
          onChange={(event) => setWebhookUrl(event.target.value)}
          placeholder={WEBHOOK_COPY.urlPlaceholder}
          disabled={isSaving || isDisabling}
          maxLength={500}
          error={isUrlError ? error : undefined}
        />
        <div className={styles.actions}>
          <ButtonComponent
            ctaText={WEBHOOK_COPY.save}
            variant="primary"
            className="white bg-blue600"
            onClick={saveConfig}
            disabled={isDisabling || !hasUrlChanged}
            loading={isSaving}
          />
          {config && (
            <>
              <ButtonComponent
                ctaText={WEBHOOK_COPY.rotateSecret}
                variant="secondary"
                className="slate900 bg-slate200"
                onClick={saveConfig}
                disabled={isSaving || isDisabling}
              />
              <ButtonComponent
                ctaText={WEBHOOK_COPY.disable}
                variant="outline"
                className="red600"
                startIcon={<TrashIcon className="icon red600" />}
                onClick={disableConfig}
                disabled={isSaving}
                loading={isDisabling}
              />
            </>
          )}
        </div>
      </div>
    </section>
  );
};

export default WebhookConfigTab;
